import { IMessagingService } from "../services";
import { debounce, getFocusable } from "../utils";
import { Component } from "./component";
class HeaderComponent extends Component
{
    private headerEl: HTMLElement;

    // Desktop Nav
    private megaNavEl: HTMLElement;
    private navListEl: HTMLElement;
    private navItems: Map<string, {
        navTrigger: HTMLElement,
        navTarget: HTMLElement,
        tabindexTargets: HTMLElement[]
    }>;
    private navItemsLabels: string[];
    private selectedNavTarget: string;

    // Mobile Nav
    private mobileNavContainerEl: HTMLElement;
    private mobileNavEl: HTMLElement;
    private mobileNavTriggerEl: HTMLElement;
    private observemobileNavResize: ResizeObserver;

    // Header Container
    private headerContainerEl: HTMLElement;

    constructor(componentEl: HTMLElement, messagingService: IMessagingService) {
        super(componentEl, messagingService);

        // bind elements
        this.headerEl = componentEl;

        // bind elements - desktop menu
        this.megaNavEl = this.headerEl.querySelector<HTMLElement>(".js-meganav");
        this.navListEl = this.megaNavEl.querySelector<HTMLElement>(".js-meganav-list");

        // bind elements - mobile menu
        this.mobileNavContainerEl = this.headerEl.querySelector<HTMLElement>(".js-mobile-nav-container");
        if (this.mobileNavContainerEl) {
            this.mobileNavEl = this.mobileNavContainerEl.querySelector<HTMLElement>(".js-mobile-nav");
            this.mobileNavTriggerEl = this.mobileNavContainerEl.querySelector<HTMLElement>(".js-mobile-nav-toggle");
        }

        // bind elements - header container
        this.headerContainerEl = this.headerEl.closest(".js-header-container");

        /*
        * Desktop Menu
        */
        // store nav items
        this.navItems = new Map();
        this.navItemsLabels = [];
        this.megaNavEl.querySelectorAll<HTMLElement>("button[aria-controls]").forEach((button) => {
            const buttonControls = button.getAttribute("aria-controls");
            if (this.navItems.get(buttonControls)) {
                return; // Duplicate nav item ID
            }
            const buttonTarget = componentEl.querySelector<HTMLElement>(`#${buttonControls}`);
            if (!buttonTarget) {
                return;  // no target sub nav panel found with id '${buttonControls}'
            }

            // Get focusable elements
            const focusableEls = getFocusable(buttonTarget) as HTMLElement[];

            this.navItemsLabels.push(buttonControls);

            this.navItems.set(buttonControls, {
                navTrigger: button,
                navTarget: buttonTarget,
                tabindexTargets: focusableEls
            });
        });

        /*
        * Mobile Menu
        */
        if (!this.mobileNavContainerEl) {
            return; // no mobile nav found
        }

        // initialise component
        this.init();
    }

    protected async init() {

        /*
        * Desktop Menu
        */
        // set defaults
        this.selectedNavTarget = "";

        // event handlers
        this.navListEl.addEventListener("click", (e: MouseEvent) => {
            const trigger = e.target as HTMLElement;
            if (!trigger.getAttribute("aria-controls")) {
                return;
            }
            const triggerIsExpanded = trigger.getAttribute("aria-expanded") === "true";
            const triggerControls = trigger.getAttribute("aria-controls");
            this.closeAll();
            if (!triggerIsExpanded) {
                this.openItem(triggerControls);
            }
        });
        
        this.megaNavEl.addEventListener("keydown", (e: KeyboardEvent) => {
            const shiftKeyPressed = e.shiftKey;
            switch(e.key) {
            case "Escape":
                if (this.selectedNavTarget) {
                    this.focusNavItem();
                }
                break;
            case "Tab":
                if (this.navItems.get(this.selectedNavTarget)) {
                    if (!shiftKeyPressed) {
                        if (e.target === [...this.navItems.get(this.selectedNavTarget).tabindexTargets].pop()) {
                            const currentIndex = this.navItemsLabels.indexOf(this.selectedNavTarget);
                            if (this.navItemsLabels[currentIndex + 1]) {
                                this.focusNavItem(this.navItemsLabels[currentIndex + 1]);
                                e.preventDefault();
                            } else {
                                this.focusNavItem();
                            }
                        }
                    } else {
                        const target = e.target as HTMLElement;
                        if (target.getAttribute("tabindex") === "-1" || target === this.navItems.get(this.selectedNavTarget).tabindexTargets[0]) {
                            e.preventDefault();
                            this.focusNavItem();
                        }
                    }
                }
                break;
            }
        });

        document.addEventListener("click", (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if ((!target.closest(".js-meganav-list") && !target.closest(".js-meganav-sub-nav")) || (target.closest(".js-meganav-sub-nav") && target.tagName == "A")) {
                this.closeAll();
            }
        });

        /*
        * Mobile Menu
        */
        const focusableMobileNavEls = getFocusable(this.mobileNavEl) as HTMLElement[];
        const lastFocusableMobileNavEls = [...focusableMobileNavEls].pop()
        
        this.mobileNavContainerEl.addEventListener("keydown", (e: KeyboardEvent) => {
            const shiftKeyPressed = e.shiftKey;
            switch(e.key) {
            case "Escape":
                this.closeMobileMenu();
                break;
            case "Tab":
                if (e.target === lastFocusableMobileNavEls && !shiftKeyPressed) {
                    e.preventDefault();
                    this.mobileNavTriggerEl.focus();
                }
                break;
            }
        });

        this.mobileNavTriggerEl.addEventListener("click", (e: MouseEvent) => {
            this.mobileNavTriggerEl.getAttribute("aria-expanded") === "false" ? this.openMobileMenu() : this.closeMobileMenu(false);
        });

        if ('ResizeObserver' in window) {
            const headerContainer = this.headerContainerEl ? this.headerContainerEl : this.headerEl;
            this.observemobileNavResize = new ResizeObserver(debounce(() => {
                this.setScrollOffset();
            }, 500));
            this.observemobileNavResize.observe(headerContainer);
        }

    }

    /*
    * Desktop Menu
    */
    public openItem(targetID: string) {
        this.selectedNavTarget = targetID;
        const { navTrigger, navTarget} = this.navItems.get(targetID);
        navTrigger.setAttribute("aria-expanded", "true");
        navTarget.setAttribute("aria-hidden", "false");
        setTimeout(function() { navTarget.focus() }, 0); // setTimeout addresses a Firefox bug
    }

    public focusNavItem(target: string = this.selectedNavTarget) {
        this.navItems.get(target).navTrigger.focus();
        this.closeAll();
        this.selectedNavTarget = "";
    }

    public closeAll() {
        this.navItems.forEach(({ navTrigger, navTarget}) => {
            navTrigger.setAttribute("aria-expanded", "false");
            navTarget.setAttribute("aria-hidden", "true");
        });
    }

    /*
    * Mobile Menu
    */
    public openMobileMenu() {
        document.body.classList.toggle('js-with-nav-open');
        this.mobileNavEl.setAttribute("aria-hidden", "false");
        this.mobileNavTriggerEl.setAttribute("aria-expanded", "true");
        this.mobileNavEl.focus({
            preventScroll: true
        });
    }
    public closeMobileMenu(setTriggerFocus: boolean = true) {
        document.body.classList.toggle('js-with-nav-open');
        this.mobileNavEl.setAttribute("aria-hidden", "true");
        this.mobileNavTriggerEl.setAttribute("aria-expanded", "false");
        if (setTriggerFocus) {
            this.mobileNavTriggerEl.focus();
        }
    }

    public setScrollOffset() {
        let scrollOffset = 20;
        if (getComputedStyle(this.headerContainerEl).position === "sticky") {
            const {height: headerContainerElHeight} = this.headerContainerEl ? this.headerContainerEl.getBoundingClientRect() : this.headerEl.getBoundingClientRect();
            scrollOffset += headerContainerElHeight;

            if (this.mobileNavEl) {
                // Just in case mobile nav is open
                const {height: mobileNavElHeight} = this.mobileNavEl.getBoundingClientRect();
                scrollOffset -= mobileNavElHeight;
            }
        }
        // Make sure offest doesn't exceed screen height (-100px)
        scrollOffset = Math.min(scrollOffset, window.innerHeight - 100);
        document.documentElement.style.setProperty("--scroll-padding", `${scrollOffset.toString()}px`);
    }

};

export { HeaderComponent };
